// src/contexts/ChatbotContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { CircularProgress, Box, Typography } from '@mui/material';

const ChatbotContext = createContext();

export const useChatbots = () => {
  return useContext(ChatbotContext);
};

export const ChatbotProvider = ({ children }) => {
  const [chatbots, setChatbots] = useState([]);
  const [activeChatbot, setActiveChatbot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const fetchChatbots = useCallback(async (callback) => {
    console.log('Fetching chatbots...');
    setIsLoading(true);
    try {
      const response = await api.get('/chatbots');
      const fetchedBots = response.data.data || [];
      console.log('Fetched chatbots:', fetchedBots.length);
      setChatbots(fetchedBots);

      if (callback) {
        callback(fetchedBots);
      } else {
        initializeActiveBot(fetchedBots);
      }
    } catch (err) {
      console.error("Failed to fetch chatbots:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initializeActiveBot = useCallback((bots) => {
    console.log('Initializing active bot from:', bots.length, 'bots');

    let userData = {};
    const userStr = localStorage.getItem('user');
    if (userStr) {
        userData = JSON.parse(userStr);
    }
    if (!('activeChatbotId' in userData)){
        userData.activeChatbotId = null
    }
    let activeBotInList = null;
    
    if (userData.activeChatbotId) {
      // Try to find by ID first, then by chatbot_identity
      activeBotInList = bots.find(b => 
        b.id.toString() === userData.activeChatbotId || 
        b.chatbot_identity === userData.activeChatbotId
      );
    }
    
    if (activeBotInList) {
      console.log('Found stored active bot:', activeBotInList.chatbot_name);
      setActiveChatbot(activeBotInList);
    } else if (bots.length > 0) {
      const defaultBot = bots.find(b => b.is_default) || bots[0];
      console.log('Setting default bot:', defaultBot.chatbot_name);
      setActiveChatbot(defaultBot);
      userData.activeChatbotId = defaultBot.id.toString()
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      console.log('No bots available');
      setActiveChatbot(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchChatbots();
    } else {
      setIsLoading(false);
    }
  }, [fetchChatbots]);

  const switchActiveChatbot = useCallback((chatbotId, options = {}) => {
    const { navigateToDashboard = false } = options;
    
    console.log('Switching to chatbot:', chatbotId, 'navigate:', navigateToDashboard);
    
    // Find by ID or chatbot_identity
    const newActiveBot = chatbots.find(b => 
      b.id.toString() === chatbotId.toString() || 
      b.chatbot_identity === chatbotId.toString()
    );
    
    if (newActiveBot) {
      console.log('Found bot to switch to:', newActiveBot.chatbot_name);
      setActiveChatbot(newActiveBot);
      localStorage.setItem('activeChatbotId', newActiveBot.id.toString());
      
      // Only navigate if explicitly requested
      if (navigateToDashboard) {
        console.log('Navigating to dashboard for:', newActiveBot.chatbot_identity);
        navigate(`/chatbot/${newActiveBot.chatbot_identity}/dashboard`);
      }
    } else {
      console.warn('Could not find chatbot with ID:', chatbotId);
    }
  }, [chatbots, navigate]);

  const refreshChatbots = useCallback((callback) => {
    console.log('Refreshing chatbots...');
    fetchChatbots(callback);
  }, [fetchChatbots]);
  
  const createNewChatbot = async (name) => {
    console.log('Creating new chatbot:', name);
    const response = await api.post('/chatbots', { chatbot_name: name });
    const newBot = response.data.data;
    // After creating, fetch the new list
    refreshChatbots();
    return newBot;
  };

  const value = {
    chatbots,
    activeChatbot,
    isLoading,
    switchActiveChatbot,
    refreshChatbots,
    createNewChatbot,
  };

  // Only show global loading on initial load
  if (isLoading && chatbots.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Application...</Typography>
      </Box>
    );
  }

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};